// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for modifying asset ownership events.
 * This module provides a focused interface for updating
 * ownership transfer details, including the owner, date,
 * and optional sale price. It handles both internal transfers
 * and customer sales with appropriate validation.
 *
 * Key features:
 * - Owner selection
 * - Date validation
 * - Price tracking
 * - Error handling
 * - Real-time updates
 */
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { deleteAssetOwnership } from '@/asset/api/delete-asset-ownership';
import { AssetOwnershipEvent } from '@/asset/api/types';
import {
  AssetOwnershipParameters,
  updateAssetOwnershipEvent,
} from '@/asset/api/update-asset-ownership-event';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormCompanySearch } from '@/components/form-company-search';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useHandleAPIError } from '@/hooks/use-handle-api-error';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating form input data.
 * Enforces required fields and data formats.
 * Owner must be a valid company, date is required,
 * and sale price is optional but must be valid if provided.
 * Date validation includes checks to prevent future dates and dates before January 1, 1970.
 */
const formSchema = z.object({
  owner: z.object(
    { id: z.string(), name: z.string(), isCustomer: z.boolean() },
    { message: 'Owner is required' }
  ),
  date: dateSchema
    .nullable()
    .refine((date) => date !== null, {
      message: 'Effective date is required',
    })
    .refine((date) => date === null || date >= MINIMUM_DATE, {
      message: 'Ownership date cannot be before January 1, 1970',
    })
    .refine(
      (date) => {
        if (date === null) {
          return true;
        }
        const limit = DateTime.now().endOf('day').plus({ days: 1 });
        return date <= limit;
      },
      { message: 'Ownership date cannot be in the future' }
    ),
  salePrice: clearableCurrencyInputSchema,
});

/**
 * Type definition for form data.
 * Used for both input and output validation.
 *
 * @typedef {Object} FormData
 */
type FormData = Omit<z.infer<typeof formSchema>, 'salePrice'> & {
  salePrice: SerializableCurrency;
};
type FormOutput = z.output<typeof formSchema>;

/**
 * Props for configuring the UpdateAssetOwnershipDialog component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The unique identifier for the asset.
   * Used for API calls and query invalidation.
   */
  assetKey: string;

  /**
   * The asset's own currency. A sale is recorded in it, so the price input is
   * locked to it rather than offering a choice.
   */
  costCurrencyCode: IsoCurrencyCode;

  /**
   * The ownership event to modify.
   * Contains current owner and transfer details.
   */
  event: AssetOwnershipEvent;

  /**
   * If true, the dialog is open and visible.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog requests to be closed.
   */
  onClose: () => void;
}

/**
 * A dialog component for updating asset ownership events.
 * Provides a form interface for modifying ownership transfer
 * details with validation and error handling.
 *
 * Features:
 * - Company search for owner selection
 * - Date validation with picker
 * - Optional sale price tracking
 * - Form state management
 * - Error message display
 * - Real-time query invalidation
 *
 * Usage example:
 * ```tsx
 * function AssetView({ assetKey, event }: Props) {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <UpdateAssetOwnershipDialog
 *       assetKey={assetKey}
 *       event={event}
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *     />
 *   );
 * }
 * ```
 */
export function UpdateAssetOwnershipDialog(props: Props) {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  const confirmationDialog = useDisclosure();

  const updateMutation = useMutation({
    mutationFn: (parameters: AssetOwnershipParameters) => {
      return updateAssetOwnershipEvent(
        props.assetKey,
        props.event.key,
        parameters
      );
    },
    onSuccess: ({ event }) => {
      queryClient.invalidateQueries({
        queryKey: ['asset', props.assetKey],
      });

      close();

      form.reset({
        owner:
          event.customer === null
            ? { ...user.organization, isCustomer: false }
            : { ...event.customer, isCustomer: true },
        date: event.date,
        salePrice: toCurrencyInputValue(
          event.salePrice,
          props.costCurrencyCode
        ),
      });
    },
    onError: (error) => {
      const apiError = handleAPIError(error);

      if (
        // Check for ownership date in future error
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_invalid_ownership_date
      ) {
        form.setError('date', {
          type: 'custom',
          message: 'Ownership date cannot be in the future',
        });
      } else if (
        // Check for duplicate ownership date error
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_duplicate_ownership_date
      ) {
        form.setError('date', {
          type: 'custom',
          message:
            'An ownership change already exists for this asset on the given date',
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      return deleteAssetOwnership(props.assetKey, props.event.key);
    },
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['asset', props.assetKey, 'ownership'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['asset', props.assetKey],
        }),
      ]);
      confirmationDialog.close();
      close();
    },
    onError: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['asset', props.assetKey, 'ownership'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['asset', props.assetKey],
        }),
      ]);

      confirmationDialog.close();
      close();
    },
  });

  const { control, handleSubmit, ...form } = useForm<
    FormData,
    unknown,
    FormOutput
  >({
    resolver: zodResolver(formSchema),
    defaultValues: {
      owner:
        props.event.customer === null
          ? { ...user.organization, isCustomer: false }
          : { ...props.event.customer, isCustomer: true },
      date: props.event.date,
      // Seeded in the asset's currency so the locked field opens unchanged.
      salePrice: toCurrencyInputValue(
        props.event.salePrice,
        props.costCurrencyCode
      ),
    },
  });

  const { dirtyFields } = form.formState;

  /**
   * Closes the dialog and resets form state.
   * Cleans up mutation state and form values.
   */
  const close = () => {
    form.reset();
    updateMutation.reset();
    props.onClose();
  };

  /**
   * Handles form submission when validation passes.
   * Only updates fields that have been modified.
   *
   * @param data - The validated form data
   */
  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    const parameters: AssetOwnershipParameters = {};

    if (dirtyFields.owner) {
      parameters.customerId = data.owner.id;
    }

    if (dirtyFields.date) {
      parameters.date = data.date ?? undefined;
    }

    if (dirtyFields.salePrice) {
      parameters.salePrice = data.salePrice ?? null;
    }

    updateMutation.mutate(parameters);
  };

  const updateError = useHandleAPIError(updateMutation.error);

  const hasOwnershipDateError =
    updateError !== null &&
    (updateError.apiStatusCode ===
      ERROR_CODE_MAPPING.asset_invalid_ownership_date ||
      updateError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_duplicate_ownership_date);

  const showGenericError = updateError !== null && !hasOwnershipDateError;

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Update ownership</DialogTitle>
        <DialogContent dividers>
          <SupportSnackbar
            isOpen={showGenericError}
            message="An error occurred while updating the asset ownership."
            onClose={updateMutation.reset}
          />
          <Stack spacing={2}>
            <FormCompanySearch
              disableClearable
              name="owner"
              label="Owner"
              control={control}
              showManufacturer={false}
            />
            <FormDatePicker
              disableFuture
              name="date"
              label="Effective date"
              control={control}
            />
            <FormCurrencyTextField
              fullWidth
              name="salePrice"
              label="Sale price (optional)"
              control={control}
              lockTo={props.costCurrencyCode}
              disabledTooltip="A sale is recorded in the asset's own currency"
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Box>
            <Button
              size="small"
              onClick={confirmationDialog.open}
              color={'error'}
            >
              Delete
            </Button>
          </Box>
          <Box>
            <Button size="small" onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="small"
              variant="contained"
              disabled={updateMutation.isPending}
            >
              Update
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={confirmationDialog.isOpen}
        onClose={confirmationDialog.close}
      >
        <DialogTitle>Delete sale record</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={1}>
            Are you sure you want to delete this sale record? The data will be
            permanently removed from the asset's history.
          </Typography>
          <Typography>
            (Note: your audit log will continue to show a record of the deleted
            sale.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={confirmationDialog.close}>
            No, cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate()}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
